import type { ImportResourceResult } from '../types';

/**
 * Aggregate success/failure counts for a completed (or partially completed) import, derived from the
 * authoritative per-resource `job3Import.resourceResults` the Importer records when continue-on-error
 * is enabled.
 */
export interface ImportResultSummary {
    /** Resources that imported successfully. */
    imported: number;
    /** Resources that failed to import. */
    failed: number;
    /** Total resources attempted (imported + failed). */
    total: number;
}

/**
 * Computes an "X of Y resources imported" summary from the per-resource import results. Returns
 * `null` when there are no per-resource results to summarise (e.g. a planning-phase failure that
 * never attempted individual resources, or an older migration without the breakdown), so callers can
 * omit the summary entirely rather than render a misleading "0 of 0".
 */
export function computeImportResultSummary(
    results: ImportResourceResult[] | undefined | null
): ImportResultSummary | null {
    if (!results || results.length === 0) {
        return null;
    }

    let imported = 0;
    let failed = 0;
    for (const result of results) {
        if (result.success) {
            imported += 1;
        } else {
            failed += 1;
        }
    }

    return { imported, failed, total: results.length };
}
